import { useState } from "react";
import { X, Clock, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "./AuthContext";

/**
 * TrialBanner — shown when days_left <= 3.
 * Dismissed per-session via sessionStorage.
 *
 * Props:
 *  onSubscribe  fn (required) — called when "Choose a plan" is clicked;
 *               parent is responsible for opening TrialEndedModal.
 */
export default function TrialBanner({ onSubscribe }) {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem("bloom-trial-banner-dismissed") === "1"
  );

  // Only show during active trial with 3 or fewer days left
  if (!user) return null;
  if (user.subscription_status !== "trialing") return null;
  if (user.days_left === null || user.days_left > 3) return null;
  if (dismissed) return null;

  const daysLeft = user.days_left;
  const urgency  = daysLeft === 0
    ? "Today is your last day"
    : `${daysLeft} day${daysLeft !== 1 ? "s" : ""} left`;

  function dismiss() {
    sessionStorage.setItem("bloom-trial-banner-dismissed", "1");
    setDismissed(true);
  }

  return (
    <div className="flex items-center justify-between gap-3 bg-warning/15 px-4 py-2.5 text-sm">
      <div className="flex items-center gap-2 font-semibold text-warning-ink">
        <Clock size={16} weight="bold" className="shrink-0" />
        <span>
          <span className="font-extrabold">{urgency}</span> on your free trial.{" "}
          <button
            onClick={onSubscribe}
            className="inline-flex items-center gap-1 font-extrabold underline underline-offset-2 hover:no-underline"
          >
            Choose a plan <ArrowRight size={13} weight="bold" />
          </button>
        </span>
      </div>
      <button
        onClick={dismiss}
        className="shrink-0 rounded-full p-1 hover:bg-warning/20"
        aria-label="Dismiss"
      >
        <X size={14} weight="bold" />
      </button>
    </div>
  );
}
